import React, { useRef, useState } from 'react';
import { 
  Download, 
  Copy, 
  Check, 
  TrendingUp, 
  DollarSign, 
  BarChart3, 
  Layers, 
  ShieldCheck, 
  Sparkles, 
  Building2, 
  Calendar,
  ExternalLink
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas-pro';
import { sanitizeClonedDocForPdf } from '../utils/pdfColorSanitizer';

interface FinancialModelProps {
  showToast?: (msg: string, type?: 'success' | 'info') => void;
}

export function FinancialProjectionModel({ showToast }: FinancialModelProps) {
  const [copied, setCopied] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const plainTextModel = `=====================================================
AZURA SERAPH™ // AZURAOS — 3-YEAR PROJECTED FINANCIAL MODEL
Platform: https://azuraos.app | Founder: Raksha Singh (founder@azuraos.app)
Company: Azura Seraph™ Pty Ltd | Pre-Seed Diligence Model
Target Round: $500,000 Pre-Seed Round
=====================================================

1. EXECUTIVE MODEL SUMMARY
• Year 1 (GTM & Cohort Wedge):
  - 25 Cohort Pilots ($750/batch) = $18,750
  - 250 Post-Grad Active SaaS Founders ($45/mo avg blended) = $135,000
  - Total Year 1 ARR: $153,750 (~$150k ARR)
  - Gross Margin: 86.5% ($133,000 Gross Profit)
  - COGS / Cloud Infra: $20,750

• Year 2 (GCC & International Acceleration):
  - 75 Cohort Pilots ($1,000/batch blended) = $75,000
  - 1,200 Active SaaS Founders ($32/mo avg blended) = $460,800
  - Total Year 2 ARR: $535,800 (Base Target: ~$450k - $500k ARR)
  - Gross Margin: 88.0% ($471,500 Gross Profit)
  - COGS / Cloud Infra: $64,300

• Year 3 (Institutional Scale & Enterprise Portfolio Tiers):
  - 200 Cohort Pilots ($1,250/batch blended) = $250,000
  - 4,000 Active SaaS Founders ($30/mo avg blended) = $1,440,000
  - Multi-Batch Enterprise Licenses (10 @ $5,500/yr) = $55,000
  - Total Year 3 ARR: $1,745,000 (Conservative Target: ~$1.2M+ ARR)
  - Gross Margin: 89.5% ($1,561,000 Gross Profit)
  - COGS / Cloud Infra: $184,000

2. UNIT ECONOMICS & REVENUE STREAMS
• B2B Cohort Pilot: $750 - $1,250 per 30-day batch (25 seats + Director Suite)
• Solo Founder Core SaaS: $29/mo or $290/yr ($24/mo effective)
• Pro Multi-Entity SaaS: $79/mo ($790/yr)
• Enterprise Portfolio License: $5,500/yr per accelerator network
• Blended CAC: ~$85 (drastically suppressed by B2B2C batch acquisition)
• LTV (SaaS Founder 24-mo avg): ~$650
• LTV / CAC Ratio: ~7.6x

3. ULTRA-LEAN COGS ADVANTAGE (>85% GROSS MARGIN)
Unlike legacy SaaS platforms burdened by server-side AI inference and database scraping, AzuraOS achieves >85% margins via:
1. Client-Side Cryptographic Vault: In-browser encryption eliminates heavy cloud database compute.
2. In-Memory Air-Gapped Statement Parser: 0.05-second client execution (No Plaid credential API fees).
3. BYOK + Lightweight Gemini Flash Architecture: Server dispatcher routes tasks efficiently with zero token waste.
=====================================================`;

  const handleCopy = () => {
    navigator.clipboard.writeText(plainTextModel);
    setCopied(true);
    if (showToast) showToast('📋 3-Year Financial Model copied to clipboard!', 'success');
    setTimeout(() => setCopied(false), 2500);
  };

  const handleDownloadPdf = async () => {
    setIsGeneratingPdf(true);
    try {
      const pageEl = document.getElementById('azura-financial-model-a4-page');
      if (!pageEl) throw new Error('Financial Model PDF element not found');

      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas-pro')
      ]);

      const canvas = await html2canvas(pageEl, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        onclone: (clonedDoc) => {
          sanitizeClonedDocForPdf(clonedDoc);
          const wrapper = clonedDoc.getElementById('azura-financial-model-pdf-wrapper');
          if (wrapper) {
            wrapper.style.position = 'static';
            wrapper.style.left = '0';
            wrapper.style.top = '0';
            wrapper.style.display = 'block';
            wrapper.style.visibility = 'visible';
            wrapper.style.opacity = '1';
          }
          const p = clonedDoc.getElementById('azura-financial-model-a4-page');
          if (p) {
            p.style.position = 'static';
            p.style.visibility = 'visible';
            p.style.opacity = '1';
          }
        }
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, 210, 297, undefined, 'FAST');
      pdf.save('Azura_Seraph_3_Year_Financial_Model_Raksha_Singh.pdf');

      if (showToast) showToast('✅ 3-Year Financial Model PDF downloaded (A4 calibrated)!', 'success');
    } catch (err) {
      console.error('Financial Model PDF export error:', err);
      if (showToast) showToast('⚠️ Direct PDF export issue, opening print dialog...', 'info');
      window.print();
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Action Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-[#101314] border border-[#242A2D] rounded-2xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/30 text-[#14B8A6] flex items-center justify-center shrink-0">
            <BarChart3 size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-extrabold text-white tracking-tight">
                3-Year Projected Financial Model
              </h3>
              <span className="px-2 py-0.5 text-[9px] font-mono font-extrabold bg-teal-500/20 text-teal-400 border border-teal-500/30 rounded">
                &gt;85% GROSS MARGIN
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              Unit economics, ARR projections, and B2B2C cohort scale calibrated for investor diligence.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleCopy}
            className="px-3.5 py-2 bg-[#1A1E20] hover:bg-[#252A2D] border border-gray-700 hover:border-gray-500 text-xs font-bold rounded-xl text-gray-200 hover:text-white transition flex items-center gap-1.5 cursor-pointer"
          >
            {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} className="text-[#14B8A6]" />}
            <span>{copied ? 'Copied Model!' : 'Copy Text'}</span>
          </button>

          <button
            onClick={handleDownloadPdf}
            disabled={isGeneratingPdf}
            className="px-4 py-2 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs rounded-xl shadow-lg transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
          >
            <Download size={14} />
            <span>{isGeneratingPdf ? 'Generating PDF...' : 'Download PDF (A4)'}</span>
          </button>
        </div>
      </div>

      {/* Interactive Visual Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Year 1 Card */}
        <div className="p-5 bg-gradient-to-b from-[#141718] to-[#0E1011] border border-teal-500/30 rounded-2xl space-y-4">
          <div className="flex justify-between items-start">
            <span className="px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold bg-teal-500/20 text-teal-300 border border-teal-500/30">
              YEAR 1 • GTM WEDGE
            </span>
            <span className="text-xs font-mono text-emerald-400 font-bold">86.5% Margin</span>
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-black text-white font-mono">$153,750</div>
            <div className="text-xs text-gray-400 font-medium mt-0.5">Projected ARR Target (~$150k)</div>
          </div>
          <div className="space-y-2 pt-2 border-t border-[#222729] text-xs text-gray-300">
            <div className="flex justify-between">
              <span className="text-gray-400">Cohort Pilots (25 @ $750):</span>
              <span className="font-mono text-white font-bold">$18,750</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Active SaaS (250 @ $45/mo avg):</span>
              <span className="font-mono text-white font-bold">$135,000</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Est. Gross Profit:</span>
              <span className="font-mono text-emerald-400 font-bold">$133,000</span>
            </div>
          </div>
        </div>

        {/* Year 2 Card */}
        <div className="p-5 bg-gradient-to-b from-[#141718] to-[#0E1011] border border-teal-500/50 rounded-2xl space-y-4 relative shadow-lg">
          <div className="flex justify-between items-start">
            <span className="px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold bg-teal-500/20 text-teal-300 border border-teal-500/40">
              YEAR 2 • REGIONAL ACCELERATION
            </span>
            <span className="text-xs font-mono text-emerald-400 font-bold">88.0% Margin</span>
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-black text-teal-400 font-mono">$535,800</div>
            <div className="text-xs text-gray-400 font-medium mt-0.5">Projected ARR Target (~$450k - $500k)</div>
          </div>
          <div className="space-y-2 pt-2 border-t border-[#222729] text-xs text-gray-300">
            <div className="flex justify-between">
              <span className="text-gray-400">Cohort Pilots (75 @ $1,000):</span>
              <span className="font-mono text-white font-bold">$75,000</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Active SaaS (1,200 @ $32/mo avg):</span>
              <span className="font-mono text-white font-bold">$460,800</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Est. Gross Profit:</span>
              <span className="font-mono text-emerald-400 font-bold">$471,500</span>
            </div>
          </div>
        </div>

        {/* Year 3 Card */}
        <div className="p-5 bg-gradient-to-b from-[#141718] to-[#0E1011] border border-amber-500/40 rounded-2xl space-y-4">
          <div className="flex justify-between items-start">
            <span className="px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
              YEAR 3 • INSTITUTIONAL SCALE
            </span>
            <span className="text-xs font-mono text-emerald-400 font-bold">89.5% Margin</span>
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-black text-amber-300 font-mono">$1,745,000</div>
            <div className="text-xs text-gray-400 font-medium mt-0.5">Projected ARR Target (~$1.2M+ ARR)</div>
          </div>
          <div className="space-y-2 pt-2 border-t border-[#222729] text-xs text-gray-300">
            <div className="flex justify-between">
              <span className="text-gray-400">Cohort Pilots (200 @ $1,250):</span>
              <span className="font-mono text-white font-bold">$250,000</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Active SaaS (4,000 @ $30/mo avg):</span>
              <span className="font-mono text-white font-bold">$1,440,000</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Multi-Batch Enterprise:</span>
              <span className="font-mono text-white font-bold">$55,000</span>
            </div>
          </div>
        </div>
      </div>

      {/* 📄 CALIBRATED OFF-SCREEN CANVAS FOR 1-PAGE A4 PDF EXPORT */}
      <div 
        id="azura-financial-model-pdf-wrapper"
        className="fixed -left-[9999px] -top-[9999px] overflow-hidden pointer-events-none"
        aria-hidden="true"
      >
        <div
          id="azura-financial-model-a4-page"
          style={{
            width: '210mm',
            height: '297mm',
            maxHeight: '297mm',
            boxSizing: 'border-box',
            padding: '14mm 16mm',
            backgroundColor: '#ffffff',
            color: '#0f172a',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}
        >
          {/* Header */}
          <div style={{ borderBottom: '2.5px solid #0d9488', paddingBottom: '10px' }} className="flex justify-between items-start">
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#0f172a', letterSpacing: '-0.3px' }}>
                AZURA SERAPH™ // AZURAOS — 3-YEAR FINANCIAL PROJECTIONS
              </div>
              <div style={{ fontSize: '11px', color: '#0d9488', fontWeight: '700', marginTop: '2px' }}>
                Sovereign Financial Operating System &amp; Cohort Governance Platform
              </div>
              <div style={{ fontSize: '9px', color: '#64748b', marginTop: '3px' }}>
                Founder: Raksha Singh (founder@azuraos.app) • Production URL: https://azuraos.app • Target Round: $500,000 Pre-Seed
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: '9px', color: '#64748b', lineHeight: '1.4' }}>
              <div><strong>Status:</strong> Live Production</div>
              <div><strong>Google for Startups</strong> ($2k+)</div>
              <div><strong>Gross Margin:</strong> &gt;85%</div>
              <div><strong>MoR:</strong> Dodo Payments</div>
            </div>
          </div>

          {/* Section 1: Executive Summary Table */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a', textTransform: 'uppercase', marginBottom: '6px', borderBottom: '1px solid #cbd5e1', paddingBottom: '2px' }}>
              1. Three-Year Financial Summary &amp; Milestone Growth
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', textAlign: 'left' }}>
              <thead>
                <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '1.5px solid #94a3b8' }}>
                  <th style={{ padding: '6px 8px', fontWeight: 'bold', color: '#334155' }}>Metric / Milestone</th>
                  <th style={{ padding: '6px 8px', fontWeight: 'bold', color: '#0f766e' }}>Year 1 (Wedge)</th>
                  <th style={{ padding: '6px 8px', fontWeight: 'bold', color: '#0f766e' }}>Year 2 (Expansion)</th>
                  <th style={{ padding: '6px 8px', fontWeight: 'bold', color: '#0f766e' }}>Year 3 (Scale)</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '5px 8px', fontWeight: '600' }}>Cohort Batches / Pilots</td>
                  <td style={{ padding: '5px 8px', color: '#0f172a' }}>25 Batches ($750/ea)</td>
                  <td style={{ padding: '5px 8px', color: '#0f172a' }}>75 Batches ($1,000/ea)</td>
                  <td style={{ padding: '5px 8px', color: '#0f172a' }}>200 Batches ($1,250/ea)</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '5px 8px', fontWeight: '600' }}>Cohort Upfront Revenue</td>
                  <td style={{ padding: '5px 8px', color: '#0f172a' }}>$18,750</td>
                  <td style={{ padding: '5px 8px', color: '#0f172a' }}>$75,000</td>
                  <td style={{ padding: '5px 8px', color: '#0f172a' }}>$250,000</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '5px 8px', fontWeight: '600' }}>Active Retained Founders (SaaS)</td>
                  <td style={{ padding: '5px 8px', color: '#0f172a' }}>250 Active Founders</td>
                  <td style={{ padding: '5px 8px', color: '#0f172a' }}>1,200 Active Founders</td>
                  <td style={{ padding: '5px 8px', color: '#0f172a' }}>4,000 Active Founders</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '5px 8px', fontWeight: '600' }}>Recurring SaaS ARR</td>
                  <td style={{ padding: '5px 8px', color: '#0f172a' }}>$135,000</td>
                  <td style={{ padding: '5px 8px', color: '#0f172a' }}>$460,800</td>
                  <td style={{ padding: '5px 8px', color: '#0f172a' }}>$1,440,000</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '5px 8px', fontWeight: '600' }}>Enterprise Portfolio Licenses</td>
                  <td style={{ padding: '5px 8px', color: '#0f172a' }}>—</td>
                  <td style={{ padding: '5px 8px', color: '#0f172a' }}>Included in pilots</td>
                  <td style={{ padding: '5px 8px', color: '#0f172a' }}>$55,000 (10 Hubs)</td>
                </tr>
                <tr style={{ backgroundColor: '#f0fdfa', borderBottom: '2px solid #0d9488', fontWeight: 'bold' }}>
                  <td style={{ padding: '6px 8px', color: '#0f766e' }}>Total Annual Run-Rate (ARR)</td>
                  <td style={{ padding: '6px 8px', color: '#0f766e' }}>$153,750 (~$150k)</td>
                  <td style={{ padding: '6px 8px', color: '#0f766e' }}>$535,800 (~$450k-$500k)</td>
                  <td style={{ padding: '6px 8px', color: '#0f766e' }}>$1,745,000 (~$1.2M+)</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '5px 8px', color: '#64748b' }}>Estimated COGS / Cloud Infra</td>
                  <td style={{ padding: '5px 8px', color: '#64748b' }}>$20,750</td>
                  <td style={{ padding: '5px 8px', color: '#64748b' }}>$64,300</td>
                  <td style={{ padding: '5px 8px', color: '#64748b' }}>$184,000</td>
                </tr>
                <tr style={{ fontWeight: 'bold', backgroundColor: '#fafaf9' }}>
                  <td style={{ padding: '6px 8px', color: '#15803d' }}>Gross Profit Margin (%)</td>
                  <td style={{ padding: '6px 8px', color: '#15803d' }}>86.5% ($133k)</td>
                  <td style={{ padding: '6px 8px', color: '#15803d' }}>88.0% ($471k)</td>
                  <td style={{ padding: '6px 8px', color: '#15803d' }}>89.5% ($1.56M)</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Section 2: Unit Economics & B2B2C Advantage */}
          <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '7px', padding: '10px 12px' }}>
            <div style={{ fontSize: '10.5px', fontWeight: 'bold', color: '#334155', textTransform: 'uppercase', marginBottom: '4px' }}>
              2. Unit Economics &amp; Capital-Efficient Flywheel
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', fontSize: '8.5px', lineHeight: '1.4' }}>
              <div>
                <strong style={{ color: '#0f172a' }}>Low Blended CAC (~$85)</strong>
                <p style={{ color: '#475569', margin: '2px 0 0' }}>B2B2C wedge sells 1 cohort pilot to an accelerator director, immediately acquiring 25 active founders at $30/seat acquisition cost.</p>
              </div>
              <div>
                <strong style={{ color: '#0f172a' }}>High Founder LTV (~$650)</strong>
                <p style={{ color: '#475569', margin: '2px 0 0' }}>Post-graduation founder retention averages 24+ months on self-serve tiers ($29/mo core or $79/mo multi-entity) with minimal churn.</p>
              </div>
              <div>
                <strong style={{ color: '#0f172a' }}>LTV / CAC Ratio (~7.6x)</strong>
                <p style={{ color: '#475569', margin: '2px 0 0' }}>Exceptional capital efficiency driven by institutional distribution partnerships rather than noisy paid ad campaigns.</p>
              </div>
            </div>
          </div>

          {/* Section 3: Why Our Margins Exceed 85% */}
          <div style={{ backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '7px', padding: '10px 12px' }}>
            <div style={{ fontSize: '10.5px', fontWeight: 'bold', color: '#0f766e', textTransform: 'uppercase', marginBottom: '4px' }}>
              3. Architectural Moat Delivering &gt;85% Gross Margins
            </div>
            <div style={{ fontSize: '8.5px', color: '#134e4a', lineHeight: '1.45', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div>• <strong>Client-Side Cryptographic Vault:</strong> User financial ledgers are encrypted in-browser; zero expensive centralized database compute or heavy background telemetry.</div>
              <div>• <strong>Air-Gapped In-Memory Statement Parser:</strong> Statements parse in &lt;0.05 seconds client-side. Completely avoids recurring third-party bank credential API scraping fees (Zero Plaid tax).</div>
              <div>• <strong>BYOK + Server AI Dispatcher:</strong> Power users provide their own keys across Gemini, Claude, OpenAI, and DeepSeek, with server-side Flash fallbacks minimizing token burn.</div>
            </div>
          </div>

          {/* Section 4: Use of Funds ($500,000 Pre-Seed) */}
          <div style={{ borderLeft: '3px solid #0d9488', paddingLeft: '10px' }}>
            <div style={{ fontSize: '10.5px', fontWeight: 'bold', color: '#0f172a' }}>4. Capital Deployment ($500,000 Pre-Seed Round)</div>
            <p style={{ fontSize: '8.5px', color: '#334155', margin: '3px 0 0', lineHeight: '1.4' }}>
              • <strong>45% ($225k) — GTM &amp; Accelerator Partnerships:</strong> Dedicated B2B sales development to secure 25–35 accelerator partnerships across F6S, GCC/Qatar, UK, and US networks.<br />
              • <strong>35% ($175k) — Engineering &amp; Direct Banking Feeds:</strong> Integrations with regional open-banking rails (GCC, Plaid US, UK/EU Open Banking) and GCC Zakat/Tax compliance modules.<br />
              • <strong>20% ($100k) — Operations &amp; Customer Success:</strong> 18-month operational runway, security audits, and dedicated cohort onboarding management.
            </p>
          </div>

          {/* Footer */}
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '6px', fontSize: '8.5px', color: '#94a3b8' }} className="flex justify-between items-center">
            <div>Page 1 of 1 • © 2026 Azura Seraph™ Pty Ltd (Reg: 2024/560516/07) • Confidential Diligence</div>
            <div>https://azuraos.app • Contact: founder@azuraos.app</div>
          </div>
        </div>
      </div>
    </div>
  );
}
